import type { ComponentCharacterCompassSubTrait } from '@edwin-group/cj-graphql/generated/graphql';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useCharacterCompassStore } from '../store/useCharacterCompassStore';

export function useCircleContainer() {
  const [positionWheel, setPositionWheel] = useState(-90);
  const { traits, Trait, SubTrait, onUpdateTrait, onUpdateSubTrait, slices, onUpdateSlices } =
    useCharacterCompassStore();
  const sliceCount = slices.length;
  const radius = 160;
  const gradient1From = Trait?.attributes?.typeSection.mainGradientFrom;
  const center = radius;

  const sliceAngle = 360 / sliceCount;
  const textRadius = radius * 0.6;

  useEffect(() => {
    setPositionWheel(-90);
  }, [slices]);

  const onClickSlice = async (startAngle: number, index: number) => {
    const targetAngle = -(startAngle + sliceAngle / 2) + 270;
    setPositionWheel(targetAngle);

    if (!Trait) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const SubTrait = traits[index].attributes?.subTrait || [];
      onUpdateTrait(traits[index]);
      onUpdateSlices(SubTrait as ComponentCharacterCompassSubTrait[], traits[index]);
      setPositionWheel(-90);
    }

    if (Trait && !SubTrait) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      onUpdateSubTrait(Trait?.attributes?.subTrait?.[index] as ComponentCharacterCompassSubTrait);
    }
  };

  const generateGradients = () => {
    return slices.map((gradient, index) => {
      const startAngle = sliceAngle * (index + 1) - 90;
      const endAngle = sliceAngle * (index + 1) - 90;

      const newCenter = center;
      const middle = (startAngle + endAngle) / 2;

      const gradientTransform = `rotate(${middle + 90} ${newCenter} ${newCenter})`;

      const gradientId = `${gradient.id}`.replace(/\s+/g, '');

      return (
        <linearGradient
          key={gradientId}
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={newCenter}
          y1={newCenter}
          x2={newCenter}
          y2={newCenter - radius}
          gradientTransform={gradientTransform}>
          <stop offset="0%" stopColor={gradient.end} />
          <stop offset="100%" stopColor={gradient.start} />
        </linearGradient>
      );
    });
  };

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const generateSlices = () => {
    return slices.map((gradient, index) => {
      const startAngle = sliceAngle * index;
      const endAngle = sliceAngle * (index + 1);
      const gradientId = `url(#${gradient.id})`.replace(/\s+/g, '');

      const x1 = center + radius * Math.cos(toRad(startAngle));
      const y1 = center + radius * Math.sin(toRad(startAngle));
      const x2 = center + radius * Math.cos(toRad(endAngle));
      const y2 = center + radius * Math.sin(toRad(endAngle));

      return (
        <motion.path
          key={index + (slices[index].trait || '')}
          onClick={() => onClickSlice(startAngle, index)}
          d={`M${center},${center} L${x1},${y1} A${radius},${radius} 0 0,1 ${x2},${y2} Z`}
          fill={gradientId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1, duration: 1 }}
        />
      );
    });
  };

  const generateText = () => {
    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      };
    };

    const describeArc = (
      x: number,
      y: number,
      radius: number,
      startAngle: number,
      endAngle: number,
      sweepFlag: string,
    ) => {
      const start = polarToCartesian(x, y, radius, endAngle - 2);
      const end = polarToCartesian(x, y, radius, startAngle);

      const largeArcFlag = Math.abs(endAngle - 2 - startAngle) <= 180 ? '0' : '1';

      const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, sweepFlag, end.x, end.y].join(' ');

      return d;
    };

    return Array.from({ length: sliceCount }).map((_, index) => {
      const rotate: { [key: number]: number } = {
        7: 5,
        6: 10,
        5: 35,
        4: 0,
        3: -63,
        2: -179,
      };
      const angle = sliceAngle * (index + 1) + sliceAngle / 2;

      // Adjust the text radius as needed
      const curvedTextRadius = textRadius + 45;

      const angleExtent = sliceAngle;
      let startAngle = (rotate[sliceCount] || 0) + angle - angleExtent / 2;
      let endAngle = angle + angleExtent / 2;

      // Default sweep flag
      let sweepFlag = '0';

      if (
        index === 0 ||
        index === slices.length - 1 ||
        (slices.length >= 6 &&
          (index === 0 || index === 1 || index === slices.length - 1 || index === slices.length - 2))
      ) {
        [startAngle, endAngle] = [endAngle, startAngle];
        sweepFlag = '1';
      }

      const pathId = `textPath${slices[index].trait}`;
      const rotateAngle = sliceCount === 7 ? 30 : 0;
      const pathData = describeArc(
        center,
        center,
        curvedTextRadius,
        startAngle + rotateAngle + 10,
        endAngle + rotateAngle + 5,
        sweepFlag,
      );

      return (
        <React.Fragment key={slices[index].trait}>
          <defs>
            <path id={pathId} d={pathData} stroke="blue" strokeWidth="2" fill="none" />
          </defs>
          <text
            style={{
              fontSize: '14px',
              fill: 'black',
              fontWeight: '500',
              pointerEvents: 'none',
            }}>
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
              {`${slices[index].trait}`}
            </textPath>
          </text>
        </React.Fragment>
      );
    });
  };

  return {
    positionWheel,
    traits,
    Trait,
    SubTrait,
    onUpdateSlices,
    radius,
    generateGradients,
    generateSlices,
    generateText,
    gradient1From,
  };
}
